/** Same-origin API client. In development Vite proxies /v1 to the API; in production the app is served next to it. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly details?: unknown,
    public readonly requestId?: string,
  ) {
    super(code);
    this.name = "ApiError";
  }
}

const base = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | undefined>;
}

/** Absolute URL for a GET the browser opens directly, such as a CSV export. */
export function apiUrl(
  path: string,
  query: Record<string, string | number | undefined> = {},
): string {
  const url = new URL(base + path, window.location.origin);
  for (const [k, v] of Object.entries(query))
    if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
  return url.toString();
}

export async function api<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const url = new URL(base + path, window.location.origin);
  for (const [k, v] of Object.entries(options.query ?? {}))
    if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
  // A form (a file) goes as multipart, with the boundary the browser sets.
  const form = options.body instanceof FormData ? options.body : null;
  const res = await fetch(url.toString(), {
    method: options.method ?? "GET",
    credentials: "include",
    headers: {
      accept: "application/json",
      ...(options.body !== undefined && !form
        ? { "content-type": "application/json" }
        : {}),
    },
    body:
      form ??
      (options.body !== undefined ? JSON.stringify(options.body) : undefined),
  });
  const text = await res.text();
  const json = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  // Required two-step verification is turned on before anything else.
  if (
    res.status === 403 &&
    json.error === "mfa_required" &&
    window.location.pathname !== "/security"
  )
    window.location.assign(
      `/security?next=${encodeURIComponent(window.location.pathname)}`,
    );
  if (!res.ok)
    throw new ApiError(
      res.status,
      String(json.error ?? `http_${res.status}`),
      json.error === "gate_review" ? json.decision : json.details,
      typeof json.request_id === "string" ? json.request_id : undefined,
    );
  return json as T;
}

export function wsUrl(path: string): string {
  const u = new URL(base + path, window.location.origin);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  return u.toString();
}
